import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';

import { getCampaignDetails } from 'api/CampaignAPI';
import { getDataFeeds } from 'api/DataFeedsAPI';
import { fetchOrganizationMembers } from 'api/OrganizationAPI';
import {
  cloneTemplate,
  copyTemplateToCampaign,
  createBeeTemplate,
  createTemplate,
  getSenders,
  getTemplateDataById,
  getTemplates,
  GetTemplatesResponse,
  importTemplate,
  TemplateForSelection,
} from 'api/TemplatePickerAPI';
import { TemplateType } from 'constants/templateTypes';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { SagaIterator } from 'redux-saga';
import * as actionTypes from './actionTypes';
import {
  cloneTemplateError,
  fetchCampaignDetailsError,
  fetchCampaignDetailsResponse,
  fetchDataFeedsError,
  fetchDataFeedsResponse,
  fetchMemberDataError,
  fetchMemberDataResponse,
  fetchSendersError,
  fetchSendersResponse,
  fetchTemplateDetailsResponse,
  fetchTemplatesResponse,
  importTemplateResponse,
} from './actions';
import { getCampaignTemplateIdFromCampaignId } from './selectors';

export function* fetchTemplateDetailsSaga({
  templateId,
  messageMedium,
}: {
  templateId: number;
  messageMedium: string;
}) {
  try {
    const { data } = yield call(getTemplateDataById as any, templateId);
    yield put(
      fetchTemplateDetailsResponse(templateId, {
        ...data.metadata,
        ...data.content,
        ...data?.extendedMetadata,
        messageMedium,
      }),
    );
  } catch (error) {
    yield put(addNegativeToast('Unable to fetch template details'));
  }
}

export function* fetchTemplatesSaga({
  filter,
  messageMedium,
  orderBy,
  page,
  pageSize,
  placementId,
  queryString,
  sorting,
  templateType,
  whatsAppTemplateType,
}: {
  filter: string;
  messageMedium: string;
  orderBy: string;
  page: number;
  pageSize: number;
  placementId: number;
  queryString: string;
  sorting: string;
  templateType: string;
  whatsAppTemplateType?: TemplateType;
}) {
  try {
    const { data } = yield call(
      getTemplates,
      messageMedium,
      orderBy,
      page,
      pageSize,
      sorting,
      templateType,
      filter,
      placementId,
      whatsAppTemplateType,
    );

    const response = data as GetTemplatesResponse;

    // all templates are stored by their id in templates.templatesById
    const templatesById = response.results.reduce(
      (byIds: { [key: number]: TemplateForSelection }, template: TemplateForSelection) => ({
        ...byIds,
        [template.id]: template,
      }),
      {} as { [key: number]: TemplateForSelection },
    );

    // but we also keep a record of the specific template ids requested
    // during _this_ call to the backend.
    const templateIds = response.results.map(({ id }) => id);

    // they are stored in a separate place, which allows us to easily
    // reuse the templates in redux state rather than fetch from the backend
    // if the provided query has been run before
    yield put(fetchTemplatesResponse(templatesById, queryString, templateIds, data.total));

    // a separate saga now gets fired for a different backend api call that provides more information about the template.
    // in the future we can rewrite this with graphql so that we get ALL of this information ALL at once.
    // the future is bright, my friends.
    // but for now, the information we need comes from two separate endpoints.
    yield all(
      templateIds.map((templateId) =>
        call(fetchTemplateDetailsSaga, { templateId, messageMedium }),
      ),
    );
  } catch (error) {
    yield put(addNegativeToast('Unable to fetch the templates'));
  }
}

export function* createTemplateSaga({
  campaignId,
  messageMedium,
}: {
  campaignId: number;
  messageMedium: string;
}) {
  try {
    // TODO: remove this info toast once a better interaction has been coded.
    yield put(addPositiveToast('Creating new template. Hold on...'));
    const { data } = yield call(createTemplate, campaignId, messageMedium);

    // creating a template returns the editTemplate page.
    // For the moment, this is necessary because the new flow isn't globally enabled.
    // Once it is globally enabled, a new endpoint can just return the templateId created.
    // but for now we need to take the templateId like so.

    const templateId = data?.globals?.__templateId__;

    // hacky way to access the window and load an angular page
    // from here. This will go away when templates are reactified.
    yield window && window.location.assign(`/templates/edit?templateId=${templateId}`);
  } catch (error) {
    yield put(addNegativeToast('Unable to create template'));
  }
}

export function* createBeeTemplateSaga({
  campaignId,
  slug,
  messageMedium,
}: {
  campaignId: number;
  slug: string;
  messageMedium: string;
}) {
  try {
    // TODO: remove this info toast once a better interaction has been coded.
    yield put(addPositiveToast('Creating new template. Hold on...'));
    const { data } = yield call(createBeeTemplate as any, campaignId, messageMedium, slug);

    // creating a template returns the editTemplate page.
    // For the moment, this is necessary because the new flow isn't globally enabled.
    // Once it is globally enabled, a new endpoint can just return the templateId created.
    // but for now we need to take the templateId like so.

    const templateId = data?.globals?.__templateId__;

    // hacky way to access the window and load an angular page
    // from here. This will go away when templates are reactified.
    yield window && window.location.assign(`/templates/edit?templateId=${templateId}`);
  } catch (error) {
    yield put(addNegativeToast('Unable to create template'));
  }
}

export function* importTemplateSaga({
  campaignId,
  template,
  onSuccess,
}: {
  campaignId: number | undefined;
  template: string | File;
  onSuccess?: (templateId?: string) => void;
}) {
  try {
    const { data } = yield call(importTemplate, campaignId, template);
    yield put(importTemplateResponse());

    /**
     * If we request an unvalid url, response is 200 but no template id is created.
     * So no template is actually imported.
     */
    const templateId = data?.globals?.__templateId__;
    if (!templateId) {
      yield put(addNegativeToast('Unable to import template'));
      return;
    }

    const templateName = template instanceof File ? template.name : `Template from ${template}`;
    yield put(
      addPositiveToast(`Success! ${templateName} has been successfully imported into Templates.`),
    );
    if (onSuccess) {
      onSuccess(templateId);
    }
  } catch (error) {
    yield put(addNegativeToast('Unable to import template'));
  }
}

export function* cloneTemplateSaga({
  templateId,
  campaignId,
}: {
  templateId: number;
  campaignId: number;
}) {
  try {
    // TODO: remove this info toast once a better interaction has been coded.
    yield put(addPositiveToast('Cloning template. Hold on...'));
    const { data } = yield call(cloneTemplate, templateId, campaignId);

    // creating a template returns the editTemplate page.
    // For the moment, this is necessary because the new flow isn't globally enabled.
    // Once it is globally enabled, a new endpoint can just return the templateId created.
    // but for now we need to take the templateId like so.

    const id = data?.globals?.__templateId__;

    // hacky way to access the window and load an angular page
    // from here. This will go away when templates are reactified.
    yield window && window.location.assign(`/templates/edit?templateId=${id}`);
  } catch (error) {
    yield put(addNegativeToast('Unable to clone template'));
    yield put(cloneTemplateError());
  }
}

export function* fetchSendersSaga() {
  try {
    const { data } = yield call(getSenders);
    const { smsSenders, authedEmails } = data;
    yield put(fetchSendersResponse(smsSenders, authedEmails));
  } catch (error) {
    yield put(addNegativeToast('Unable to fetch senders'));
    yield put(fetchSendersError());
  }
}

export function* fetchCampaignDetailsSaga({ campaignId }: { campaignId: number }): SagaIterator {
  try {
    const campaign = yield call(getCampaignDetails, campaignId);
    yield put(fetchCampaignDetailsResponse(campaign));
  } catch (error) {
    yield put(addNegativeToast('Unable to fetch the campaign templates'));
    yield put(fetchCampaignDetailsError());
  }
}

export function* copyTemplateToCampaignSaga({
  campaignId,
  templateId,
  dataFeedId,
}: {
  campaignId: number;
  templateId: number;
  dataFeedId: number;
}): SagaIterator {
  try {
    // TODO: remove this info toast once a better interaction has been coded.
    yield put(addPositiveToast('Setting the template for the campaign. Hold on...'));
    yield call(copyTemplateToCampaign, campaignId, templateId, dataFeedId);
    const campaignTemplateId = yield select((state) =>
      getCampaignTemplateIdFromCampaignId(state, { campaignId }),
    );

    // hacky way to access the window and load an angular page
    // from here. This will go away when templates are reactified.
    yield window?.location?.assign(
      `/templates/edit?templateId=${campaignTemplateId}&campaignId=${campaignId}`,
    ) as any;
  } catch (error) {
    yield put(addNegativeToast('Unable to create template'));
  }
}

export function* fetchDataFeedsSaga() {
  try {
    const { data } = yield call(getDataFeeds);
    yield put(fetchDataFeedsResponse(Object.values(data)));
  } catch (error) {
    yield put(fetchDataFeedsError());
  }
}

export function* fetchMemberDataSaga({ memberIds }: { memberIds: string[] }) {
  try {
    const { data } = yield call(fetchOrganizationMembers, {
      memberIdFilter: memberIds,
    });
    const membersById = data.members.reduce((acc: any, member: { userId: any }) => {
      return { ...acc, [member.userId]: member };
    }, {});
    yield put(fetchMemberDataResponse(membersById));
  } catch (error) {
    yield put(fetchMemberDataError());
  }
}

addToRootSaga([
  // @ts-ignore
  takeEvery(actionTypes.FETCH_TEMPLATES_REQUEST, fetchTemplatesSaga),
  // @ts-ignore
  takeLatest(actionTypes.CREATE_TEMPLATE_REQUEST, createTemplateSaga),
  // @ts-ignore
  takeLatest(actionTypes.CREATE_BEE_TEMPLATE_REQUEST, createBeeTemplateSaga),
  // @ts-ignore
  takeLatest(actionTypes.IMPORT_TEMPLATE_REQUEST, importTemplateSaga),
  // @ts-ignore
  takeLatest(actionTypes.CLONE_TEMPLATE_REQUEST, cloneTemplateSaga),
  // @ts-ignore
  takeEvery(actionTypes.FETCH_TEMPLATE_DETAILS_REQUEST, fetchTemplateDetailsSaga),
  // @ts-ignore
  takeEvery(actionTypes.FETCH_CAMPAIGN_DETAILS_REQUEST, fetchCampaignDetailsSaga),
  takeLatest(actionTypes.FETCH_SENDERS_REQUEST, fetchSendersSaga),
  // @ts-ignore
  takeLatest(actionTypes.COPY_TEMPLATE_TO_CAMPAIGN_REQUEST, copyTemplateToCampaignSaga),
  takeEvery(actionTypes.FETCH_DATA_FEEDS_REQUEST, fetchDataFeedsSaga),
  // @ts-ignore
  takeEvery(actionTypes.FETCH_MEMBER_DATA_REQUEST, fetchMemberDataSaga),
]);
